import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ShoppingCart, MapPin, ChevronDown, Send, Receipt, User, LogOut, Globe, Shield, Store, Bike, Contrast, Wallet as WalletIcon, Moon, Sun, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import TrendingMarquee from './TrendingMarquee';
import { CURRENCIES } from '../mock';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import LanguageSwitcher from './LanguageSwitcher';
import SocialShareBar from './SocialShareBar';
import ActiveOrdersDropdownSection from './ActiveOrdersDropdownSection';
import api from '../services/api';

const Header = ({ onSearch, showSearch = true }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { currency, setCurrency, currencyOptions, cartCount } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [query, setQuery] = useState('');
  // Mobile search expansion: on phones, the search field is hidden behind an
  // icon to keep the header uncluttered. Tapping the icon expands it into a
  // full-width row that overlays the address pill and currency dropdown.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Persistent mono (grey/white/black) theme toggle. Images stay in color;
  // only chrome/text/borders/rings/gradients desaturate via `mono.css`.
  const [monoMode, setMonoMode] = useState(() => {
    try { return localStorage.getItem('shipitnow_mono') === '1'; } catch { return false; }
  });

  // Wrap any theme flip with a temporary `html.theme-transitioning` class so
  // background/text/border transitions cross-fade smoothly instead of snap.
  const withThemeTransition = (fn) => {
    const root = document.documentElement;
    root.classList.add('theme-transitioning');
    fn();
    // 260ms > 200ms transition → guarantees the fade finishes before we drop
    // the class, otherwise a subsequent unrelated repaint would inherit the
    // transition and hurt perf.
    window.setTimeout(() => root.classList.remove('theme-transitioning'), 260);
  };

  useEffect(() => {
    const root = document.documentElement;
    if (monoMode) root.classList.add('mono');
    else root.classList.remove('mono');
    try { localStorage.setItem('shipitnow_mono', monoMode ? '1' : '0'); } catch { /* ignore */ }
  }, [monoMode]);

  // Persistent dark theme. Independent of mono — the two compose cleanly.
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const v = localStorage.getItem('shipitnow_dark');
      if (v === '1') return true;
      if (v === '0') return false;
      // First-time visit: fall back to the OS preference.
      return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches === true;
    } catch { return false; }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) root.classList.add('dark');
    else root.classList.remove('dark');
    try { localStorage.setItem('shipitnow_dark', darkMode ? '1' : '0'); } catch { /* ignore */ }
  }, [darkMode]);

  const handleSearch = (e) => {
    setQuery(e.target.value);
    onSearch && onSearch(e.target.value);
  };

  const initials = (user?.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  // Fetch the primary store name for vendors so we can show it under the
  // user's name in the header dropdown. Silent on failure — the dropdown
  // still renders correctly without the store name if the fetch fails.
  const [storeName, setStoreName] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!user?.vendor_id) { setStoreName(null); return; }
    api.get('/vendor-admin/store').then((r) => {
      if (alive) setStoreName(r.data?.name || null);
    }).catch(() => { if (alive) setStoreName(null); });
    return () => { alive = false; };
  }, [user?.vendor_id]);

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3 sm:gap-5">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src="/shipitnow-logo.png" alt="ShipitNow" className="h-8 w-auto" />
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger className="hidden md:flex items-center gap-1.5 px-3 h-9 rounded-lg hover:bg-slate-100 text-sm text-slate-700">
            <MapPin className="h-4 w-4 text-emerald-600" />
            <span className="font-medium">Ottawa</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Delivery address</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>Ottawa, Ontario</DropdownMenuItem>
            <DropdownMenuItem>Ibadan, Oyo</DropdownMenuItem>
            <DropdownMenuItem className="text-emerald-700">+ Add new address</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {showSearch && (
          <>
            {/* Desktop: inline search bar */}
            <div className="relative flex-1 max-w-xl hidden sm:block">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={handleSearch}
                placeholder={t('common.search_placeholder', 'Search restaurants, groceries, dishes…')}
                className="pl-9 h-10 bg-slate-50 border-slate-200 focus-visible:bg-white"
              />
            </div>
            {/* Mobile: spacer + icon trigger */}
            <div className="flex-1 sm:hidden" />
            <button
              type="button"
              onClick={() => setMobileSearchOpen(true)}
              className="sm:hidden h-10 w-10 rounded-lg hover:bg-slate-100 flex items-center justify-center"
              aria-label="Search"
              data-testid="mobile-search-trigger"
            >
              <Search className="h-5 w-5 text-slate-700" />
            </button>
          </>
        )}
        {!showSearch && <div className="flex-1" />}

        <button
          onClick={() => nav('/marketplace')}
          className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-lg hover:bg-slate-100 text-sm font-medium text-slate-700"
          data-testid="header-marketplace-btn"
        >
          <ShoppingBag className="h-4 w-4 text-emerald-600" /> Marketplace
        </button>

        <button
          onClick={() => nav('/cart')}
          className="relative h-10 w-10 rounded-lg hover:bg-slate-100 flex items-center justify-center"
          aria-label="Cart"
        >
          <ShoppingCart className="h-5 w-5 text-slate-700" />
          {cartCount > 0 && (
            <Badge className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-emerald-600 hover:bg-emerald-600 text-white text-[11px]">
              {cartCount}
            </Badge>
          )}
        </button>

        <div className="block">
          <LanguageSwitcher />
        </div>

        <button
          onClick={() => withThemeTransition(() => setDarkMode((v) => !v))}
          className={`hidden sm:flex items-center justify-center h-10 w-10 rounded-lg transition-colors ${darkMode ? 'bg-amber-400 text-slate-900 hover:bg-amber-500' : 'hover:bg-slate-100 text-slate-700'}`}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-pressed={darkMode}
          title={darkMode ? 'Light mode' : 'Dark mode'}
          data-testid="dark-theme-toggle"
        >
          {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        <button
          onClick={() => withThemeTransition(() => setMonoMode((v) => !v))}
          className={`hidden sm:flex items-center justify-center h-10 w-10 rounded-lg transition-colors ${monoMode ? 'bg-slate-900 text-white hover:bg-slate-800' : 'hover:bg-slate-100 text-slate-700'}`}
          aria-label={monoMode ? 'Switch to color theme' : 'Switch to grey / white / black theme'}
          aria-pressed={monoMode}
          title={monoMode ? 'Colorful theme' : 'Grey · white · black theme (photos stay in color)'}
          data-testid="mono-theme-toggle"
        >
          <Contrast className="h-5 w-5" />
        </button>

        <div className="hidden sm:block">
          <SocialShareBar compact />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 h-10 pl-1 pr-2 rounded-full hover:bg-slate-100" data-testid="user-menu-trigger">
            {user?.avatar ? (
              <img src={user.avatar} alt="avatar" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 hidden sm:block" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72" data-testid="user-menu-content">
            <DropdownMenuLabel>
              <div className="font-medium text-slate-900 truncate" data-testid="user-menu-name">{user?.name}</div>
              <div className="text-xs text-slate-500 font-normal truncate" data-testid="user-menu-email">{user?.email}</div>
              {storeName && (
                <div
                  className="mt-1 text-[11px] font-extrabold uppercase tracking-wider truncate"
                  style={{ color: '#065F46' }}
                  data-testid="user-menu-store-name"
                  title={storeName}
                >
                  <Store className="inline h-3 w-3 mr-1 -mt-0.5" />
                  {storeName}
                </div>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase font-semibold tracking-wider text-slate-400">Active orders</div>
            <ActiveOrdersDropdownSection />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav('/orders')} data-testid="menu-orders">
              <Receipt className="h-4 w-4 mr-2" /> {t('nav.orders', 'Orders')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav('/marketplace')} data-testid="menu-marketplace">
              <ShoppingBag className="h-4 w-4 mr-2" /> {t('nav.marketplace', 'Marketplace')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav('/wallet')} data-testid="menu-wallet">
              <WalletIcon className="h-4 w-4 mr-2" /> {t('nav.wallet', 'Wallet & gift cards')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav('/profile')} data-testid="menu-profile">
              <User className="h-4 w-4 mr-2" /> {t('nav.profile', 'Profile')}
            </DropdownMenuItem>

            {/* Theme pill — visible on mobile where the Header buttons are hidden. */}
            <div className="sm:hidden px-2 py-2" data-testid="menu-theme-pill">
              <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 mb-1.5">Theme</div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); withThemeTransition(() => setDarkMode((v) => !v)); }}
                  className={`h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-colors ${darkMode ? 'bg-amber-400 text-slate-900 border-amber-500' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                  aria-pressed={darkMode}
                  data-testid="menu-dark-toggle"
                >
                  {darkMode ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                  {darkMode ? 'Light' : 'Dark'}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); withThemeTransition(() => setMonoMode((v) => !v)); }}
                  className={`h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-colors ${monoMode ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                  aria-pressed={monoMode}
                  data-testid="menu-mono-toggle"
                >
                  <Contrast className="h-3.5 w-3.5" />
                  {monoMode ? 'Color' : 'Mono'}
                </button>
              </div>
            </div>
            <DropdownMenuSeparator className="sm:hidden" />
            <DropdownMenuItem onClick={() => nav('/driver')} data-testid="menu-driver-portal">
              <Bike className="h-4 w-4 mr-2" /> {t('nav.driver_portal', 'Driver portal')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {user?.vendor_id && (
              <>
                <DropdownMenuItem onClick={() => nav('/vendor-admin')} className="text-emerald-700 focus:text-emerald-800" data-testid="menu-manage-store">
                  <Store className="h-4 w-4 mr-2" /> {t('nav.manage_store', 'Manage store')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {user?.is_admin && (
              <>
                <DropdownMenuItem onClick={() => nav('/admin')} className="text-emerald-700 focus:text-emerald-800" data-testid="menu-admin-portal">
                  <Shield className="h-4 w-4 mr-2" /> {t('nav.admin_portal', 'Admin portal')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => { logout(); nav('/login'); }} className="text-rose-600 focus:text-rose-700" data-testid="menu-sign-out">
              <LogOut className="h-4 w-4 mr-2" /> {t('nav.sign_out', 'Sign out')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Trending marquee — scrolling vendor/product/review ribbon (mobile + desktop) */}
      <TrendingMarquee />

      {/* Mobile expanded search overlay */}
      {mobileSearchOpen && (
        <MobileSearchOverlay
          query={query}
          onChange={handleSearch}
          onClose={() => setMobileSearchOpen(false)}
          placeholder={t('common.search_placeholder', 'Search restaurants, groceries, dishes…')}
        />
      )}
    </header>
  );
};

/**
 * Mobile search overlay with a swipe-down-to-close gesture.
 * The user can either tap Cancel or drag downward >60px on the drag handle / overlay
 * to dismiss — matches native iOS sheet behavior.
 */
const MobileSearchOverlay = ({ query, onChange, onClose, placeholder }) => {
  const startYRef = React.useRef(null);
  const dragYRef = React.useRef(0);            // current drag distance (not via setState so onTouchEnd reads fresh)
  const sheetRef = React.useRef(null);
  const onTouchStart = (e) => { startYRef.current = e.touches[0].clientY; dragYRef.current = 0; };
  const onTouchMove = (e) => {
    if (startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    if (dy > 0) {
      dragYRef.current = dy;
      // Direct DOM transform — avoids React state churn during the drag.
      if (sheetRef.current) sheetRef.current.style.transform = `translateY(${dy}px)`;
    }
  };
  const onTouchEnd = () => {
    const dy = dragYRef.current;
    if (sheetRef.current) sheetRef.current.style.transform = '';   // reset
    startYRef.current = null;
    dragYRef.current = 0;
    if (dy > 60) onClose();
  };
  return (
    <div
      className="sm:hidden fixed inset-0 z-50 bg-black/30 animate-in fade-in"
      data-testid="mobile-search-overlay"
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        className="absolute inset-x-0 top-0 bg-white rounded-b-2xl shadow-lg transition-transform duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* iOS-style drag handle */}
        <div className="flex justify-center pt-2.5 pb-1" data-testid="mobile-search-handle">
          <div className="h-1 w-10 rounded-full bg-slate-300" />
        </div>
        <div className="px-3 pb-3 pt-1 flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400 shrink-0" />
          <Input
            autoFocus
            value={query}
            onChange={onChange}
            placeholder={placeholder}
            className="h-10 bg-slate-50 border-slate-200 focus-visible:bg-white"
            data-testid="mobile-search-input"
          />
          <button
            onClick={onClose}
            className="text-sm text-slate-600 hover:text-slate-900 px-2 shrink-0"
            data-testid="mobile-search-close"
          >
            Cancel
          </button>
        </div>
        <p className="px-4 pb-3 text-[11px] text-slate-400">Swipe down to close</p>
      </div>
    </div>
  );
};

export default Header;
